/**
 * Infinite Scroll for Collection Product Grid
 * Automatically loads more products when user scrolls near the bottom
 */
(function() {
  'use strict';

  class InfiniteScrollCollection {
    constructor() {
      this.isLoading = false;
      this.currentPage = 1;
      this.totalPages = 1;
      this.observer = null;
      this.sentinel = null;
      this.resultsContainer = null;
      this.productsGrid = null;
      this.sectionId = null;
      this.init();
    }

    init() {
      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setup());
      } else {
        this.setup();
      }
    }

    setup() {
      const resultsContainer = document.getElementById('facets-results');
      if (!resultsContainer) return;

      const isEnabled = resultsContainer.dataset.infiniteScrollEnabled === 'true';
      if (!isEnabled) return;

      this.resultsContainer = resultsContainer;
      this.productsGrid = document.getElementById('products-grid');
      if (!this.productsGrid) return;

      // Get initial pagination state
      this.currentPage = parseInt(resultsContainer.dataset.currentPage || '1', 10);
      this.totalPages = parseInt(resultsContainer.dataset.totalPages || '1', 10);

      // Find section ID from parent
      const sectionElement = resultsContainer.closest('[id^="CustomCollectionGrid-"]');
      if (sectionElement) {
        this.sectionId = sectionElement.id.replace('CustomCollectionGrid-', '');
      }

      // Setup intersection observer for sentinel element
      this.setupObserver();

      // Re-initialize when filters/sorting change (Filterable component updates)
      this.observeFilterableUpdates();
    }

    setupObserver() {
      // Clean up existing observer
      if (this.observer) {
        this.observer.disconnect();
      }

      this.sentinel = document.getElementById('infinite-scroll-sentinel');
      if (!this.sentinel) return;

      const options = {
        root: null,
        rootMargin: '200px', // Start loading 200px before reaching bottom
        threshold: 0.1
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.isLoading && this.currentPage < this.totalPages) {
            this.loadNextPage();
          }
        });
      }, options);

      this.observer.observe(this.sentinel);
    }

    observeFilterableUpdates() {
      // Watch for Filterable component updates
      const filterableEl = document.getElementById('facets-filterable');
      if (!filterableEl) return;

      const mutationObserver = new MutationObserver((mutations) => {
        // Check if the results container was replaced
        let resultsReplaced = false;
        mutations.forEach(mutation => {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === 1 && node.id === 'facets-results') {
              resultsReplaced = true;
            }
          });
        });

        if (resultsReplaced || !document.getElementById('facets-results')) {
          // Re-setup when the results container is updated
          setTimeout(() => {
            this.reset();
            this.setup();
          }, 350); // Wait for Filterable animation to complete
        } else {
          // Check if products grid was updated
          const newProductsGrid = document.getElementById('products-grid');
          if (newProductsGrid && newProductsGrid !== this.productsGrid) {
            this.productsGrid = newProductsGrid;
            // Reset pagination state
            this.resultsContainer = document.getElementById('facets-results');
            if (this.resultsContainer) {
              this.currentPage = parseInt(this.resultsContainer.dataset.currentPage || '1', 10);
              this.totalPages = parseInt(this.resultsContainer.dataset.totalPages || '1', 10);
              this.setupObserver();
            }
          }
        }
      });

      mutationObserver.observe(filterableEl, {
        childList: true,
        subtree: true
      });
    }

    reset() {
      // Clean up existing observer
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      this.isLoading = false;
      this.sentinel = null;
      this.resultsContainer = null;
      this.productsGrid = null;
    }

    async loadNextPage() {
      if (this.isLoading || this.currentPage >= this.totalPages) return;

      this.isLoading = true;
      this.showLoading();
      this.hideError();

      try {
        const nextPage = this.currentPage + 1;
        let nextPageUrl = this.resultsContainer.dataset.nextPageUrl;
        
        if (!nextPageUrl) {
          throw new Error('Next page URL not found');
        }

        // Build URL with section_id parameter
        // Shopify pagination URLs are relative and already include filters/sorting
        // We just need to add section_id for section rendering
        const url = new URL(nextPageUrl, window.location.origin);
        
        // Add section_id for section rendering (required for AJAX section loading)
        if (this.sectionId) {
          url.searchParams.set('section_id', this.sectionId);
        }

        const response = await fetch(url.toString());
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Find the products grid in the response
        const newProductsGrid = doc.getElementById('products-grid');
        if (!newProductsGrid) {
          throw new Error('Products grid not found in response');
        }

        // Extract product items
        const productItems = newProductsGrid.querySelectorAll('li');
        if (productItems.length === 0) {
          throw new Error('No products found in response');
        }

        // Append products to existing grid
        productItems.forEach(item => {
          this.productsGrid.appendChild(item.cloneNode(true));
        });

        // Update pagination state from response
        const newResultsContainer = doc.getElementById('facets-results');
        if (newResultsContainer) {
          this.currentPage = parseInt(newResultsContainer.dataset.currentPage || nextPage.toString(), 10);
          this.totalPages = parseInt(newResultsContainer.dataset.totalPages || '1', 10);
          
          // Update next page URL
          const newNextPageUrl = newResultsContainer.dataset.nextPageUrl;
          this.resultsContainer.dataset.nextPageUrl = newNextPageUrl || '';
          this.resultsContainer.dataset.currentPage = this.currentPage.toString();
          this.resultsContainer.dataset.totalPages = this.totalPages.toString();

          // Update or remove sentinel
          const newSentinel = doc.getElementById('infinite-scroll-sentinel');
          if (newSentinel && this.currentPage < this.totalPages) {
            // Sentinel will be automatically re-observed
          } else {
            // No more pages, remove sentinel
            if (this.sentinel) {
              this.sentinel.remove();
            }
            if (this.observer) {
              this.observer.disconnect();
            }
          }
        }

        // Trigger custom event for other scripts
        document.dispatchEvent(new CustomEvent('infinite-scroll:loaded', {
          detail: { page: this.currentPage }
        }));

        // Re-initialize Alpine.js for new elements if needed
        if (window.Alpine) {
          // Alpine will automatically initialize new elements
          // But we may need to trigger a refresh for teleports/quick-buy
          const filterableEl = document.getElementById('facets-filterable');
          if (filterableEl) {
            filterableEl.dispatchEvent(new CustomEvent('shapes:section:hasmutated', {
              bubbles: true
            }));
          }
        }

      } catch (error) {
        console.error('Error loading next page:', error);
        this.showError();
      } finally {
        this.isLoading = false;
        this.hideLoading();
      }
    }

    showLoading() {
      const loadingEl = document.getElementById('infinite-scroll-loading');
      if (loadingEl) {
        loadingEl.classList.remove('hidden');
      }
    }

    hideLoading() {
      const loadingEl = document.getElementById('infinite-scroll-loading');
      if (loadingEl) {
        loadingEl.classList.add('hidden');
      }
    }

    showError() {
      const errorEl = document.getElementById('infinite-scroll-error');
      if (errorEl) {
        errorEl.classList.remove('hidden');
      }
    }

    hideError() {
      const errorEl = document.getElementById('infinite-scroll-error');
      if (errorEl) {
        errorEl.classList.add('hidden');
      }
    }
  }

  // Initialize when script loads
  if (typeof window !== 'undefined') {
    window.InfiniteScrollCollection = InfiniteScrollCollection;
    
    // Auto-initialize if results container exists
    if (document.getElementById('facets-results')) {
      new InfiniteScrollCollection();
    } else {
      // Wait for DOM if needed
      document.addEventListener('DOMContentLoaded', () => {
        if (document.getElementById('facets-results')) {
          new InfiniteScrollCollection();
        }
      });
    }
  }
})();

